<template>
  <div class="login-wrapper">
    <!-- Background con imagen -->
    <div
      class="login-background"
      :style="{
        backgroundImage: background
          ? `url(${background})`
          : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      }"
    >
      <div class="login-overlay"></div>
    </div>

    <!-- Contenedor de partículas -->
    <div id="particles-js" ref="particlesRef" class="particles-container"></div>

    <!-- Contenedor principal -->
    <div class="login-container">
      <!-- Card de login con glassmorphism -->
      <div class="login-card" :class="{ 'is-loading': loading }">
        <!-- Spinner de carga -->
        <transition name="fade">
          <div v-if="loading" class="login-loader">
            <div class="loader-spinner"></div>
          </div>
        </transition>

        <!-- Logo -->
        <div class="login-logo-container">
          <b-link :href="$store.state.customer_link" class="login-logo-link">
            <div v-if="logo" class="login-logo-wrapper">
              <img :src="logo" alt="Logo" class="login-logo" />
            </div>
            <div v-else class="login-logo-placeholder">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2L2 7L12 12L22 7L12 2Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2 17L12 22L22 17"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2 12L12 17L22 12"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </b-link>
        </div>

        <template v-if="!showForgotPassword">
          <!-- Título de bienvenida -->
          <div class="login-header">
            <h1 class="login-title">{{ loginText || "Bienvenido" }}</h1>
            <p class="login-subtitle">Ingresa tus credenciales para continuar</p>
          </div>

          <!-- Alerta de error -->
          <transition name="slide-fade">
            <div v-if="error.length > 0" class="login-alert">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
                <path
                  d="M12 8V12M12 16H12.01"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
              <span>{{ error }}</span>
              <button class="alert-close" @click="error = ''">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
            </div>
          </transition>

          <!-- Formulario -->
          <form @submit.prevent="login" class="login-form">
          <!-- Campo RUT/DNI -->
          <div
            class="input-group"
            :class="{ focused: rutFocused, 'has-value': rut.length > 0 }"
          >
            <div class="input-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <circle
                  cx="12"
                  cy="7"
                  r="4"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <input
              :id="chilean && !isDni ? 'rut' : 'dni'"
              v-model="rut"
              :placeholder="
                chilean && !isDni ? 'Ej: 12345678-9' : 'Ingrese su DNI'
              "
              class="login-input"
              @focus="rutFocused = true"
              @blur="rutFocused = false"
              @keyup="formatRut"
              autocomplete="username"
            />
            <label class="input-label">{{
              chilean && !isDni ? "RUT" : "DNI"
            }}</label>
          </div>

          <!-- Checkbox para DNI -->
          <div v-if="chilean" class="checkbox-container">
            <label class="custom-checkbox">
              <input type="checkbox" v-model="isDni" />
              <span class="checkmark"></span>
              <span class="checkbox-text">No poseo RUT chileno</span>
            </label>
          </div>

          <!-- Campo Contraseña -->
          <div
            class="input-group"
            :class="{
              focused: passwordFocused,
              'has-value': password.length > 0,
            }"
          >
            <div class="input-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="3"
                  y="11"
                  width="18"
                  height="11"
                  rx="2"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
                <path
                  d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <circle cx="12" cy="16" r="1" fill="currentColor" />
              </svg>
            </div>
            <input
              id="password"
              :type="passwordFieldType"
              v-model="password"
              placeholder="••••••••"
              class="login-input"
              @focus="passwordFocused = true"
              @blur="passwordFocused = false"
              autocomplete="current-password"
            />
            <label class="input-label">Contraseña</label>
            <button
              type="button"
              class="password-toggle"
              @click="switchVisibility"
              :aria-label="
                passwordFieldType === 'password'
                  ? 'Mostrar contraseña'
                  : 'Ocultar contraseña'
              "
            >
              <svg
                v-if="passwordFieldType === 'password'"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <svg
                v-else
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65663 6.06 6.06M9.9 4.24C10.5883 4.07888 11.2931 3.99834 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1752 15.0074 10.8016 14.8565C10.4281 14.7056 10.0887 14.4811 9.80385 14.1962C9.51897 13.9113 9.29439 13.5719 9.14351 13.1984C8.99262 12.8248 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2218 9.18488 10.8538C9.34884 10.4859 9.58525 10.1546 9.88 9.88"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M1 1L23 23"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <!-- Botón de login -->
          <button type="submit" class="login-button" :disabled="loading">
            <span class="button-text">Iniciar Sesión</span>
            <svg
              class="button-arrow"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 12H19M19 12L12 5M19 12L12 19"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <a
            href="#"
            class="reset-password-link"
            @click.prevent="openForgotPassword"
          >
            ¿Olvidaste tu contraseña?
          </a>
          </form>
        </template>
        <template v-else>
          <forgot-password-form
            :customer="$route.params.customer"
            @back="showForgotPassword = false"
          />
        </template>

        <!-- Footer del card -->
        <div class="login-footer">
          <p>Plataforma segura de acceso</p>
          <div class="security-badge">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M9 12L11 14L15 10"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>Conexión cifrada</span>
          </div>
        </div>
      </div>

      <!-- Powered by Mobysuite -->
      <a
        href="https://mobysuite.com"
        target="_blank"
        rel="noopener noreferrer"
        class="powered-by"
      >
        <span class="powered-by-text">Powered by</span>
        <span class="mobysuite-text">Mobysuite</span>
      </a>
    </div>

  </div>
</template>
<script>
import "particles.js";
import ForgotPasswordForm from "./ForgotPasswordForm.vue";

const DEFAULT_PASSWORD_WARNING_KEY = "ms_default_password_warning";

export default {
  name: "login",
  components: { ForgotPasswordForm },
  data() {
    return {
      color: "rgba(22,22,22,.4)",
      image: "",
      rut: "",
      isDni: false,
      error: "",
      password: "",
      loading: false,
      chilean: null,
      passwordFieldType: "password",
      rutFocused: false,
      passwordFocused: false,
      showForgotPassword: false,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.initParticles();
    });
  },
  beforeDestroy() {
    if (window.pJSDom && window.pJSDom.length > 0) {
      window.pJSDom[0].pJS.fn.vendors.destroypJS();
      window.pJSDom = [];
    }
  },
  methods: {
    initParticles() {
      if (window.particlesJS) {
        window.particlesJS("particles-js", {
          particles: {
            number: {
              value: 25,
              density: {
                enable: true,
                value_area: 1000,
              },
            },
            color: {
              value: "#ffffff",
            },
            shape: {
              type: "circle",
            },
            opacity: {
              value: 0.25,
              random: true,
              anim: {
                enable: true,
                speed: 0.3,
                opacity_min: 0.1,
                sync: false,
              },
            },
            size: {
              value: 2,
              random: true,
              anim: {
                enable: false,
              },
            },
            line_linked: {
              enable: false,
            },
            move: {
              enable: true,
              speed: 0.3,
              direction: "none",
              random: true,
              straight: false,
              out_mode: "out",
              bounce: false,
            },
          },
          interactivity: {
            detect_on: "canvas",
            events: {
              onhover: {
                enable: true,
                mode: "repulse",
              },
              onclick: {
                enable: false,
              },
              resize: true,
            },
            modes: {
              repulse: {
                distance: 80,
                duration: 0.4,
              },
            },
          },
          retina_detect: true,
        });
      }
    },
    login() {
      let app = this;
      app.loading = true;
      app.error = "";
      const usedDefaultPassword = app.isUsingDefaultRutPassword();
      app.$auth.login({
        data: {
          email: this.rut,
          password: this.password,
          customer_id: this.customerId,
          error: null,
        },
        success: function (msg) {
          app.loading = false;
          app.error = "";

          if (usedDefaultPassword) {
            try {
              localStorage.setItem(
                DEFAULT_PASSWORD_WARNING_KEY,
                JSON.stringify({
                  customer: app.customer,
                  ts: Date.now(),
                })
              );
            } catch (e) {
              // ignore storage failures
            }
          } else {
            try {
              localStorage.removeItem(DEFAULT_PASSWORD_WARNING_KEY);
            } catch (e) {
              // ignore storage failures
            }
          }

          app.$auth.user(msg.data.user);
        },
        error: function () {
          app.loading = false;
          app.error = app.chilean
            ? "RUT y/o Contraseña inválidos"
            : "DNI y/o Contraseña inválidos";
        },
        rememberMe: true,
        redirect: `/app/${this.$route.params.customer}`,
        fetchUser: true,
      });
    },
    goTo(link, data = null) {
      this.$router.push({ name: link, params: data });
    },
    switchVisibility() {
      this.passwordFieldType =
        this.passwordFieldType === "password" ? "text" : "password";
    },
    openForgotPassword() {
      this.showForgotPassword = true;
    },
    digito(rut, dv) {
      let suma = 0;
      let multiplicador = 2;
      while (rut !== 0) {
        if (multiplicador === 8) {
          multiplicador = 2;
        }
        suma += (rut % 10) * multiplicador;
        rut = Math.trunc(rut / 10);
        multiplicador++;
      }
      suma = 11 - (suma % 11);
      switch (suma) {
        case 11:
          return "0" === dv;
        case 10:
          return "K" === dv.toUpperCase();
        default:
          return suma.toString() === dv;
      }
    },
    formatRut() {
      if (this.chilean && !this.isDni) {
        if (this.rut.length > 0) {
          this.rut = this.rut.replace(/[^\dKk]/g, "");
          let dato = this.rut;
          let largo = dato.length;
          if (largo === 1) {
            this.dv = dato;
            this.rut = "";
            this.digito(this.rut, this.dv);
            this.formato();
          } else if (largo > 1) {
            this.dv = dato.substring(largo - 1);
            this.rut = dato.substring(largo - 1, 0);
            this.digito(this.rut, this.dv);
            this.formato();
          }
        }
      } else {
        return true;
      }
    },
    formato() {
      let rut = this.rut ? parseInt(this.rut) + "-" : "";
      this.rut = rut + this.dv;
    },

    getRutFirstSixDigits() {
      const digits = String(this.rut || "").replace(/\D/g, "");
      if (digits.length < 6) return "";

      // En RUT, normalmente el último dígito corresponde al DV.
      const body = digits.length > 1 ? digits.slice(0, -1) : digits;
      return body.slice(0, 6);
    },

    isUsingDefaultRutPassword() {
      // Solo aplica para usuarios chilenos que ingresan con RUT (no DNI)
      if (!this.chilean || this.isDni) return false;

      const firstSix = this.getRutFirstSixDigits();
      if (firstSix.length !== 6) return false;

      return String(this.password || "").trim() === firstSix;
    },
  },
  computed: {
    logo() {
      if (this.$store.getters.getCustomerData) {
        if (this.$route.params.customer === "rimaza")
          return "/images/logo/rimaza.png";
        return this.$store.getters.getCustomerData.show_logo_login
          ? this.$store.getters.getCustomerData.logo_1_url
          : null;
      }
      return null;
    },
    loginText() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.salute_login
        : "";
    },
    background() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.login_background
        : null;
    },
    customer() {
      return this.$store.getters.getCustomer;
    },
    customerId() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.customer_id
        : null;
    },
    country() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.country
        : 1;
    },
  },
  watch: {
    country: {
      handler: function () {
        this.chilean = this.country == 0 ? false : true;
      },
      immediate: true,
    },
  },
};
</script>
<style scoped>
/* ===== Variables CSS ===== */
.login-wrapper {
  --primary-color: var(--color-a, #6366f1);
  --primary-gradient: var(
    --color,
    linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)
  );
  --card-bg: rgba(255, 255, 255, 0.95);
  --text-primary: #1f2937;
  --text-secondary: #6b7280;
  --text-muted: #9ca3af;
  --border-color: #e5e7eb;
  --error-color: #ef4444;
  --success-color: #10b981;
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  --shadow-2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-xl: 24px;
  --transition-fast: 150ms cubic-bezier(0.4, 0, 0.2, 1);
  --transition-normal: 250ms cubic-bezier(0.4, 0, 0.2, 1);
  --transition-slow: 350ms cubic-bezier(0.4, 0, 0.2, 1);
}

:global(html, body) {
  height: 100%;
  background: #0f3460;
}

/* ===== Layout Base ===== */
.login-wrapper {
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  justify-content: center;
  overflow-x: hidden;
  padding: 20px;
  padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
}

.login-background {
  position: fixed;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.login-background::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(10, 22, 40, 0.75) 0%,
    rgba(26, 26, 46, 0.65) 50%,
    rgba(22, 33, 62, 0.75) 100%
  );
}

/* ===== Partículas ===== */
.particles-container {
  position: fixed;
  inset: 0;
  z-index: 2;
  pointer-events: none;
}

.login-overlay {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at center,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.3) 100%
  );
  pointer-events: none;
}

.login-container {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 440px;
  padding: 0;
  margin: auto;
}

/* ===== Card Principal ===== */
.login-card {
  position: relative;
  background: var(--card-bg);
  backdrop-filter: blur(20px);
  border-radius: var(--radius-xl);
  padding: 48px 40px;
  box-shadow: var(--shadow-2xl);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: transform var(--transition-slow),
    box-shadow var(--transition-slow);
}

.login-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 30px 60px -15px rgba(0, 0, 0, 0.3);
}

.login-card.is-loading {
  pointer-events: none;
}

/* ===== Loader ===== */
.login-loader {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  border-radius: var(--radius-xl);
  z-index: 50;
}

.loader-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== Logo ===== */
.login-logo-container {
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
}

.login-logo-link {
  display: block;
  transition: transform var(--transition-normal),
    opacity var(--transition-normal);
}

.login-logo-link:hover {
  transform: scale(1.02);
  opacity: 0.9;
}

.login-logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px 30px;
  border-radius: var(--radius-lg);
  transition: all var(--transition-normal);
  /* Liquid Glass - fondo semi-oscuro equilibrado */
  background: rgba(131, 132, 133, 0.45);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.login-logo {
  max-width: 200px;
  max-height: 80px;
  width: auto;
  height: auto;
  object-fit: contain;
}

.login-logo-placeholder {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-gradient);
  border-radius: var(--radius-lg);
  color: white;
}

/* ===== Header ===== */
.login-header {
  text-align: center;
  margin-bottom: 32px;
}

.login-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  letter-spacing: -0.025em;
}

.login-subtitle {
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin: 0;
}

/* ===== Alert ===== */
.login-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: var(--radius-md);
  color: var(--error-color);
  font-size: 0.9rem;
  margin-bottom: 24px;
}

.login-alert svg {
  flex-shrink: 0;
}

.login-alert span {
  flex: 1;
}

.alert-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--error-color);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.alert-close:hover {
  background: rgba(239, 68, 68, 0.15);
}

/* ===== Form ===== */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ===== Input Group ===== */
.input-group {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  transition: color var(--transition-fast);
  pointer-events: none;
  z-index: 1;
}

.input-group.focused .input-icon,
.input-group.has-value .input-icon {
  color: var(--primary-color);
}

.input-label {
  position: absolute;
  left: 52px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.95rem;
  color: var(--text-muted);
  pointer-events: none;
  transition: all var(--transition-normal);
  background: transparent;
  padding: 0 4px;
}

.input-group.focused .input-label,
.input-group.has-value .input-label {
  top: 0;
  left: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--primary-color);
  background: var(--card-bg);
}

.login-input {
  width: 100%;
  height: 56px;
  padding: 0 16px 0 52px;
  font-size: 1rem;
  color: var(--text-primary);
  background: transparent;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-md);
  outline: none;
  transition: border-color var(--transition-fast),
    box-shadow var(--transition-fast);
}

.login-input::placeholder {
  color: transparent;
}

.input-group.focused .login-input::placeholder,
.input-group.has-value .login-input::placeholder {
  color: var(--text-muted);
}

.login-input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
}

/* ===== Password Toggle ===== */
.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: color var(--transition-fast), background var(--transition-fast);
}

.password-toggle:hover {
  color: var(--text-secondary);
  background: rgba(0, 0, 0, 0.05);
}

/* ===== Checkbox ===== */
.checkbox-container {
  margin-top: -8px;
}

.custom-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.custom-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.checkmark {
  position: relative;
  width: 20px;
  height: 20px;
  background: transparent;
  border: 2px solid var(--border-color);
  border-radius: 6px;
  transition: all var(--transition-fast);
}

.custom-checkbox:hover .checkmark {
  border-color: var(--primary-color);
}

.custom-checkbox input:checked ~ .checkmark {
  background: var(--primary-color);
  border-color: var(--primary-color);
}

.checkmark::after {
  content: "";
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.custom-checkbox input:checked ~ .checkmark::after {
  display: block;
}

.checkbox-text {
  font-size: 0.9rem;
  color: var(--text-secondary);
}

/* ===== Button ===== */
.login-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 56px;
  padding: 0 24px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-login-btn-text, #ffffff);
  background: var(--color-login-btn, var(--primary-gradient));
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-normal);
  margin-top: 8px;
}

.login-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px -5px rgba(99, 102, 241, 0.4);
}

.login-button:active:not(:disabled) {
  transform: translateY(0);
}

.login-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-arrow {
  transition: transform var(--transition-normal);
}

.login-button:hover:not(:disabled) .button-arrow {
  transform: translateX(4px);
}

.reset-password-link {
  display: inline-block;
  align-self: flex-end;
  margin-top: 12px;
  color: var(--text-secondary);
  font-size: 0.92rem;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: color var(--transition-fast);
}

.reset-password-link:hover {
  color: var(--primary-color);
}

/* ===== Footer ===== */
.login-footer {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);
  text-align: center;
}

.login-footer p {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin: 0 0 12px 0;
}

.security-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 0.75rem;
  color: var(--success-color);
  background: rgba(16, 185, 129, 0.1);
  border-radius: 100px;
}

/* ===== Transitions ===== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--transition-normal);
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all var(--transition-normal);
}

.slide-fade-leave-active {
  transition: all var(--transition-fast);
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .login-container {
    padding: 0;
  }

  .login-card {
    padding: 36px 28px;
    border-radius: var(--radius-lg);
  }

  .login-title {
    font-size: 1.75rem;
  }

  .login-subtitle {
    font-size: 0.9rem;
  }

  .login-logo {
    max-width: 180px;
  }
}

@media (max-width: 480px) {
  .login-container {
    padding: 16px;
  }

  .login-card {
    padding: 28px 20px;
    border-radius: 20px;
  }

  .login-title {
    font-size: 1.5rem;
  }

  .login-subtitle {
    font-size: 0.85rem;
  }

  .login-logo {
    max-width: 140px;
  }

  .login-logo-container {
    margin-bottom: 20px;
  }

  .login-header {
    margin-bottom: 24px;
  }

  .login-input {
    padding: 0 52px 0 52px;
    font-size: 0.9rem;
  }

  .login-btn {
    padding: 14px 24px;
    font-size: 0.9rem;
  }

  .form-group-label {
    font-size: 0.8rem;
  }

  .login-footer {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .login-footer .forgot-link {
    text-align: center;
  }
}

@media (max-width: 375px) {
  .login-container {
    padding: 12px;
  }

  .login-card {
    padding: 24px 16px;
    border-radius: 16px;
  }

  .login-title {
    font-size: 1.35rem;
  }

  .login-logo {
    max-width: 120px;
  }

  .login-input {
    padding: 0 52px 0 52px;
    font-size: 0.85rem;
  }

  .login-btn {
    padding: 12px 20px;
    font-size: 0.85rem;
  }

  .powered-by {
    font-size: 0.7rem;
    padding: 6px 12px;
  }
}

/* Dark mode disabled: always use light mode */

/* ===== Powered by Mobysuite ===== */
.powered-by {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 24px auto 0;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 100px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 0.75rem;
  transition: all var(--transition-normal);
  width: fit-content;
}

.powered-by:hover {
  background: rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.95);
  transform: translateY(-2px);
  text-decoration: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.powered-by-text {
  font-weight: 400;
  letter-spacing: 0.02em;
}

.mobysuite-text {
  font-weight: 600;
}
</style>
